import SHA1 from 'crypto-js/sha1';
import type { OpenAPIV3 } from 'openapi-types';

import type { TableResponseBody } from '../table/Table';
import type { Branded } from '../types/Branded';
import { parseApiPath } from './path';

export type ApiScopeMethod = 'DELETE' | 'GET' | 'PATCH' | 'PUT' | 'POST';

export type ApiScopeBase = {
  method: ApiScopeMethod;
  /** Use colon notation for variables. Example: /webhooks/:key/test/:type/:action */
  path: string;
};

export type ApiScopeId = Branded<string, 'apiScopeId'>;

export type ApiScope = ApiScopeBase & {
  id: ApiScopeId;
};

export type ApiScopesGetResponseBody = TableResponseBody<ApiScope>;

export function getApiScopeId(apiScope: ApiScopeBase) {
  const path = getBackwardsCompatiblePath(parseApiPath(apiScope.path).path);

  return SHA1(`${apiScope.method} ${path}`).toString();
}

export function getApiScopeMethod(
  oasMethod: OpenAPIV3.HttpMethods,
): ApiScopeMethod {
  switch (oasMethod) {
    case 'delete':
      return 'DELETE';
    case 'get':
      return 'GET';
    case 'patch':
      return 'PATCH';
    case 'put':
      return 'PUT';
    case 'post':
      return 'POST';
    default:
      throw new Error(`Unknown ApiScope method: ${oasMethod}`);
  }
}

/**
 * Escaping of variables in the path changed from: `:variable` to `{variable}`.
 * (because path is stored in directory structure, and windows doesn't allow `:` in the path)
 *
 * This function converts the new format to the old one.
 * Because hash using old format is saved in the database, and it's not easy to change it.
 *
 * @see {https://ipfabric.atlassian.net/browse/NIM-9291}
 */
export function getBackwardsCompatiblePath(path: string) {
  return path.replace(/{(\w+)}/g, ':$1');
}
